import { createContext, useContext, SyntheticEvent } from 'react';
import { ApplicantRecruitmentOverviewResponse, ApplicantStatus, CandidateTabs } from '@/Models/CandidateOverview';
import { TestTypes } from '@/Models/Configuration';
import { AdvancedFilter } from '../../Models/AdvancedFilter';
import AdvancedFilterTypes from '@/Models/AdvancedFilterTypes';
import ApplicantsOverviewApplicantStatusFilterEnum from '../../Models/ApplicantsOverviewApplicantStatusFilterEnum';
import ApplicantsOverviewSortByEnum from '../../Models/ApplicantsOverviewSortByEnum';
import { AssessmentStatuses } from '@/Models/AssessmentData';
import ApplicantsOverviewHiringStatusFilterEnum from '../../Models/ApplicantsOverviewHiringStatusFilterEnum';

export interface CandidatesOverviewContextStateBase {
    assessmentName: string;
    assessmentStatus: AssessmentStatuses;
    clientId: string;
    candidates: ApplicantRecruitmentOverviewResponse[];
    selected: Array<ApplicantRecruitmentOverviewResponse>;
    openFilter: boolean;
    testTypes: TestTypes[];
    totalCandidatesCount: number;
    pageSize: number;
    pageNumber: number;
    tabValue: CandidateTabs;
    jobCategoryName: string;
    jobFunctionName: string;
    advancedFilters: AdvancedFilter[];
    includesExperienceCriteria: boolean;
}

export interface CandidatesOverviewContextState extends CandidatesOverviewContextStateBase {
    assessmentStatus: AssessmentStatuses;
    status?: ApplicantStatus;
    filterBy: Array<ApplicantsOverviewApplicantStatusFilterEnum>;
    hiringStatusFilter: Array<ApplicantsOverviewHiringStatusFilterEnum>;
    sortBy?: ApplicantsOverviewSortByEnum;
    search: string;
    tabValue: CandidateTabs;
}

export interface CandidatesOverviewContextType extends CandidatesOverviewContextStateBase {
    assessmentId: string;
    setSelected: (values: ApplicantRecruitmentOverviewResponse[]) => void;
    loadPageAsync: () => Promise<void>;
    sort: (value?: ApplicantsOverviewSortByEnum) => void;
    filter: (values: ApplicantsOverviewApplicantStatusFilterEnum[]) => void;
    filterByHiringStatus: (values: ApplicantsOverviewHiringStatusFilterEnum[]) => void;
    searchByName: (name: string) => void;
    saveFavoriteAsync: (candidateId: string, isFavorite: boolean) => Promise<void>;
    handlePaginationModelChange: (model: { page: number; pageSize: number }) => void;
    filterBy: Array<ApplicantsOverviewApplicantStatusFilterEnum>;
    hiringStatusFilter: Array<ApplicantsOverviewHiringStatusFilterEnum>;
    handleTabChange: (event: SyntheticEvent, value: CandidateTabs) => void;
    toggleOpenFilter: () => void;
    updateAdvancedFilter: (filter: AdvancedFilter) => void;
    resetAdvancedFilterByType: (filterType: AdvancedFilterTypes) => void;
    resetAdvancedFilter: () => void;
    search: string;
    shortlistApplicantAsync: (applicantId: string) => Promise<void>;
    interviewApplicantAsync: (applicantId: string) => Promise<void>;
    referenceCheckApplicantAsync: (applicantId: string) => Promise<void>;
    offerApplicantAsync: (applicantId: string) => Promise<void>;
    backgroundCheckApplicantAsync: (applicantId: string) => Promise<void>;
    removeApplicantAsync: (applicantId: string) => Promise<void>;
    loading: boolean;
    groupReportProcessing: boolean;
    getApplicantGroupReportResults: () => Promise<void>;
}

export const CandidatesOverviewContext = createContext<CandidatesOverviewContextType | undefined>(undefined);

export function useCandidatesOverviewContext() {
    const context = useContext(CandidatesOverviewContext);
    if (!context) {
        throw new Error('useCandidatesOverviewContext must be used within the CandidatesOverviewContext.Provider');
    }
    return context;
}
